import { Location } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from '@angular/fire/compat/firestore';
import { Timestamp, serverTimestamp } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';

import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import {
  EMPTY,
  Observable,
  defer,
  Subject,
  catchError,
  debounceTime,
  exhaustMap,
  filter,
  from,
  groupBy,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { Grade, GradeStatus } from '../../models/grade.school.model';
import { Group, GroupType } from '../../models/group.model';
import { SchoolMember } from '../../models/member.school.model';
import { MembershipStatus } from '../../models/membership.status';
import { SchoolGradeService } from '../../services/grade.school.service';
import * as gradeActions from '../actions/grade.school.actions';
import * as groupActions from '../actions/group.actions';
import * as memberActions from '../actions/member.school.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';
import * as gradeSelector from '../selectors/grade.school.selector';
import * as groupSelector from '../selectors/group.selector';

@Injectable()
export class SchoolGradeEffects implements OnDestroy {
  actions$ = inject(Actions);
  store = inject(Store<AppState>);
  gradeService: SchoolGradeService = inject(SchoolGradeService);
  toastr: ToastrService = inject(ToastrService);
  logger: NGXLogger = inject(NGXLogger);
  afs: AngularFirestore = inject(AngularFirestore);
  router: Router = inject(Router);
  location: Location = inject(Location);

  private logout$ = new Subject<void>();

  constructor() {}

  ngOnDestroy(): void {
    this.logout$.next();
    this.logout$.complete();
  }

  // Effect to fetch Grades
  getGrades$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gradeActions.getGrades),
      debounceTime(500),
      // concatMap(({ groupId }) => this.store.select(groupSelector.getSelectedGroup)),
      switchMap(({ groupId, academicYear, userMemberType, lastRecord }) =>
        defer(() => {
          this.logout$.next();
          let query: AngularFirestoreCollection<Grade> = this.afs
            .collection('groups')
            .doc(groupId)
            .collection('grades', (ref) => {
              var reference = ref
                .where('gradeStatus', '==', GradeStatus.active)
                .where('gradeYear', '==', academicYear)
                .limit(100)
                .orderBy('gradeAvgAge', 'desc');

              if (lastRecord) reference = reference.startAfter(lastRecord);

              return reference;
            });
          return query.get();
        }).pipe(
          // Errors are handled per request so one failure does not complete
          // the effect (later getGrades actions would be ignored).
          map((grades) => {
            const filtered: Grade[] = grades.docs.map((doc) => ({
              ...(doc.data() as Grade),
              id: doc.id,
            }));
            return gradeActions.addGradesToStore({ grades: filtered });
          }),
          catchError((error) => {
            this.logger.error(`getGrades$ :: ${error}`);
            this.toastr.error(
              'An error occurred. Please try again later.',
              'Error fetching Grades',
            );
            return of(gradeActions.getGradesFailure({ error }));
          }),
        ),
      ),
    ),
  );

  getGrade$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gradeActions.getGrade),
      filter((action) => !!action.gradeId && !!action.groupId),
      groupBy((action) => action.gradeId),
      mergeMap((group$) =>
        group$.pipe(
          // Use groupSelector to get the group data based on the action's groupId
          switchMap((action) =>
            this.store.select(groupSelector.getGroupById(action.groupId)).pipe(
              take(1),
              map((group) => ({ action, group })),
            ),
          ),
          switchMap(({ action, group }) => {
            this.logout$.next();

            // Now 'group' is the specific group found by getGroupById
            const isArchive = action.gradeYear !== group?.academicYear;
            const collectionName = isArchive ? 'grade_archive' : 'grades';

            this.logger.debug(
              `Fetching grade from ${collectionName} for year ${action.gradeYear}`,
            );

            return from(
              this.afs
                .collection('groups')
                .doc(action.groupId)
                .collection(collectionName)
                .ref.where('gradeYear', '==', action.gradeYear)
                .where(isArchive ? 'id' : '__name__', '==', action.gradeId)
                .get(),
            ).pipe(
              map((snap) => {
                if (snap.empty) {
                  throw new Error(`Grade not found in ${collectionName}`);
                }
                const gradeData = snap.docs[0].data() as Grade;
                return gradeActions.getGradeSuccess({
                  grade: { ...gradeData, id: snap.docs[0].id },
                });
              }),
              catchError((error) => {
                this.logger.error(`getGrade$ :: ${error}`);
                this.toastr.error(error.message, 'Error fetching Grade');
                return of(gradeActions.getGradeFailure({ error }));
              }),
            );
          }),
        ),
      ),
    ),
  );

  // Effect to fetch current route and find id of the group
  // If the group is a school, and grades do not exist in store, then fetch the grades
  fetchGrades$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.select),
      exhaustMap(({ groupType, groupId }) => {
        if (groupType !== GroupType.school) return EMPTY;

        return this.store.select(groupSelector.getSelectedGroup).pipe(
          take(1),
          switchMap((group) => {
            if (
              group === undefined ||
              group.memberStatus !== MembershipStatus.active
            ) {
              return EMPTY;
            }
            return this.store
              .select(gradeSelector.getGradeCountForSelectedGroup)
              .pipe(
                take(1),
                switchMap((count) => {
                  if (count === 0) {
                    return of(
                      gradeActions.getGrades({
                        groupId: group.id,
                        academicYear: group.academicYear,
                      }),
                    );
                  }
                  return EMPTY;
                }),
              );
          }),
        );
      }),
      catchError((error) => {
        // Handle error
        return EMPTY;
      }),
    ),
  );

  // Effect to create a grade
  create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(gradeActions.create),
      switchMap(({ grade }) => {
        const createGrade: Grade = {
          ...grade,
          created: serverTimestamp() as Timestamp,
          modified: serverTimestamp() as Timestamp,
        };
        return from(this.gradeService.create(createGrade)).pipe(
          switchMap((id) => {
            const localGrade: Grade = {
              ...grade,
              id: id,
              created: Timestamp.fromDate(moment().toDate()),
              modified: Timestamp.fromDate(moment().toDate()),
            };
            this.toastr.success(
              'The Grade has been added successfully. You can now add students to the grade.',
              'Grade Created Successfully',
            );
            return of(gradeActions.createSuccess({ grade: localGrade }));
          }),
          catchError((error) => {
            this.logger.error(`Error creating Grade: ${error}`);
            this.toastr.error(
              `An error occurred while creating Grade. Please try again later.`,
              'Error Creating Grade',
            );
            return of(gradeActions.createFailure({ error }));
          }),
        );
      }),
    ),
  );

  // Effect triggered by createSuccess
  afterCreateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gradeActions.createSuccess),
      debounceTime(1000),
      switchMap(() =>
        this.store.select(groupSelector.getSelectedGroup).pipe(take(1)),
      ),
      switchMap((group) => {
        if (group?.grades == undefined) return EMPTY;
        return of(
          groupActions.updateSuccess({
            group: { ...group, grades: group.grades + 1 },
          }),
        );
      }),
    ),
  );

  // Effect to update a grade
  update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(gradeActions.update),
      switchMap(({ id, grade }) => {
        const createGrade: Grade = {
          ...grade,
          modified: serverTimestamp() as Timestamp,
        };
        return from(this.gradeService.update(id, createGrade)).pipe(
          switchMap(() => {
            const localGrade: Grade = {
              ...grade,
              id: id,
              // The server sets the real time; keep a close local value.
              modified: Timestamp.now(),
            };
            return of(gradeActions.updateSuccess({ grade: localGrade }));
          }),
          catchError((error) => {
            // this.logger.error(`Error updating grade: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Updating Grade',
            );
            return of(gradeActions.updateFailure({ error }));
          }),
        );
      }),
    ),
  );

  reassignGrade$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(gradeActions.reassignGrade),
      withLatestFrom(this.store.select((state) => state.user.user)),
      exhaustMap(([payload, user]) =>
        from(this.gradeService.gradeReassign(payload)).pipe(
          switchMap((res) => {
            const grades = res.result.grades || [];
            const members = res.result.members || [];

            // Just trigger the updates. Don't wait here.
            const actions: Action[] = [
              ...grades.map((g: Grade) =>
                gradeActions.getGrade({
                  groupId: g.groupId,
                  gradeYear: g.gradeYear,
                  gradeId: g.id!,
                }),
              ),
              // Only fetch members that aren't "Me" (since the groups listener handles Me)
              ...members
                .filter((m: SchoolMember) => m.memberId !== user?.uid)
                .map((m: SchoolMember) =>
                  memberActions.getMember({
                    groupId: payload.groupId,
                    memberId: m.memberId,
                  }),
                ),
            ];

            return [
              ...actions,
              gradeActions.reassignGradeSuccess({
                message: 'Update initiated',
              }),
            ];
          }),
          catchError((error) =>
            of(gradeActions.reassignGradeFailure({ error })),
          ),
        ),
      ),
    ),
  );

  // effect to delete a grade
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gradeActions.deleteAction),
      switchMap(({ groupId, id }) =>
        this.gradeService
          .delete(groupId, id)
          .then(() => {
            this.toastr.success(
              'The grade has been deleted successfully.',
              'Grade Deleted',
            );
            return gradeActions.deleteSuccess({ id: id });
          })
          .catch((error: any) => {
            // this.logger.error(`Error deleting grade: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Deleting Grade',
            );
            return gradeActions.deleteFailure({ error });
          }),
      ),
    ),
  );

  // Effect triggered by deleteSuccess
  afterDeleteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gradeActions.deleteSuccess),
      debounceTime(1000),
      switchMap(() =>
        this.store.select(groupSelector.getSelectedGroup).pipe(take(1)),
      ),
      switchMap((group) => {
        if (group?.grades == undefined) return EMPTY;
        return of(
          groupActions.updateSuccess({
            group: { ...group, grades: group.grades - 1 },
          }),
        );
      }),
    ),
  );

  getGradeStats$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.select, gradeActions.loadGradeStats),
      withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
      exhaustMap(([{ groupId }, group]) => {
        // this.logout$.next();
        if (group?.groupType !== GroupType.school) return EMPTY;
        if (group?.academicYear == undefined)
          return of(groupActions.getGradeStatsDone({ group: null }));
        // this.logger.log(
        //   `getGradeStats$ :: Fetching stats for group: ${
        //     group?.id
        //   }, ${group?.academicYear.toString()}`
        // );
        return this.afs
          .collection('groups')
          .doc(group?.id)
          .collection('grade_stats')
          .doc(group?.academicYear.toString())
          .get()
          .pipe(take(1))
          .pipe(
            switchMap((snapshot) => {
              // this.logger.log(
              //   `getGradeStats$ :: Query returned ${snapshot.exists} for group`
              // );
              if (!snapshot.exists) return EMPTY;
              const record: any = snapshot.data();
              // this.logger.log(
              //   `getGradeStats$ :: Stats found...${JSON.stringify(record)}`
              // );
              let g: Group = {
                id: group?.id,
                gradeStats: record,
                gradeStatsLoaded: true,
              } as Group;

              // this.logger.log(
              //   `getGradeStats$ :: Stats to store: ${JSON.stringify(g)}`
              // );
              return of(groupActions.getGradeStatsDone({ group: g }));
            }),
            catchError((error) => {
              this.logger.log(`getGradeStats$ :: Error: ${error.toString()}`);
              return of(groupActions.getGradeStatsDone({ group: null }));
            }),
          );
      }),
    ),
  );

  clearGroupGrades$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.clearFromStore),
      switchMap(({ id }) =>
        this.store.select(gradeSelector.getGradeIdsByGroup(id)).pipe(
          take(1),
          switchMap((grades) => {
            if (grades.length > 0) {
              return of(gradeActions.clearFromStore({ ids: grades }));
            }
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  clearGrades$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(gradeActions.clear());
      }),
    ),
  );
}
